import { Button } from '@hologram-dimension/button';
import { DropdownPanel } from '@hologram-dimension/dropdown-panel';
import { Icon } from '@hologram-dimension/icon';
import clsx from 'clsx/lite';
import { ACCOUNT_LOGIN, ORGS_CREATE_STEP1, withContext } from 'common-js/constants/paths';
import { useAppDispatch, useAppSelector } from 'common-js/hooks';
import { OrgModel } from 'common-js/models';
import { logout } from 'common-js/reducers/account/actions';
import {
  selectImpersonator,
  selectName,
  selectUserEmail,
} from 'common-js/reducers/account/selectors';
import { stopImpersonating } from 'common-js/reducers/admin/actions';
import { changeContext, getAllBilling } from 'common-js/reducers/organization/actions';
import {
  getSelectedOrg,
  getSelectedOrgIndex,
  getUnSelectedOrgs,
} from 'common-js/reducers/organization/selectors';
import { useCallback, useState, type FC } from 'react';
import { Link, browserHistory } from 'react-router';

const OrgChange: FC = () => {
  const dispatch = useAppDispatch();

  const accountName = useAppSelector(selectName);
  const selectedOrg = useAppSelector(getSelectedOrg) || new OrgModel();
  const unSelectedOrgs = useAppSelector(getUnSelectedOrgs) || [];
  const userEmail = useAppSelector(selectUserEmail);
  const impersonator = useAppSelector(selectImpersonator);

  const userLogout = useCallback(() => {
    dispatch(logout()).then(() => {
      browserHistory.push(ACCOUNT_LOGIN);
    });
  }, [dispatch]);

  const changeOrg = useCallback(
    async (orgId) => {
      await dispatch(changeContext(orgId, true, null));
      dispatch(getAllBilling()).catch(() => Promise.resolve());
    },
    [dispatch]
  );

  const [open, setOpen] = useState(false);
  const toggleDropdownVisible = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);
  const toggleDropdownVisibleKey = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        toggleDropdownVisible();
      }
    },
    [toggleDropdownVisible]
  );

  const selectedOrgIndex = useAppSelector(getSelectedOrgIndex);

  return (
    <div className="orgChange">
      <DropdownPanel
        open={open}
        onOpenChange={(newOpen) => setOpen(newOpen)}
        placement="top-end"
        type="custom"
        className="orgChangePanel"
        content={
          <div className={`selectedOrgIdx${selectedOrgIndex % 7}`}>
            <div className="orgChange">
              <div className="dropdown">
                <div className="dropdown-container direction-down-cover active">
                  <div className="dropdown-button grid-row vertical-align primary-logo">
                    <div className="user-logo current">
                      <div className="user-logo-letter">{selectedOrg.initial}</div>
                    </div>
                    <span className="logo-label account-fullname">
                      <div>{selectedOrg.isMyAccount ? accountName : selectedOrg.name}</div>
                      <div className="account-orgid">Org id {selectedOrg.id}</div>
                    </span>
                  </div>
                  <div className="header logged-in-as">
                    Logged in as <span>{userEmail}</span>
                  </div>
                  <div className="switch-heading">
                    Switch organization
                    <Icon
                      className="switch-heading__icon"
                      name="ArrowNortheastSouthwest"
                      size="small"
                      fill="DdsColorIconDefault"
                    />{' '}
                  </div>
                  <div className="other-orgs scrollable">
                    {unSelectedOrgs
                      .sort((last) => (last.isMyAccount ? -1 : 1))
                      .map((org) => (
                        <div
                          key={org.id}
                          className="dropdown-button grid-row vertical-align secondary-logo"
                        >
                          <Button
                            variant="tertiary"
                            onClick={() => changeOrg(org.id)}
                            className="logo-label other-orgs-button"
                          >
                            {org.isMyAccount ? accountName : org.name}
                          </Button>
                        </div>
                      ))}
                    <div className="dropdown-btns">
                      <Link
                        className="Button Button--secondary Button--has-icon Button--has-icon-left Button--full-width"
                        to={withContext(ORGS_CREATE_STEP1)}
                      >
                        <div className="Button__content">
                          <div className="Button__icon Button__icon--left">
                            <Icon name="OrganizationAdd" size="medium" fill="DdsColorIconDefault" />
                          </div>
                          <span className="Button__text">Create organization</span>
                        </div>
                      </Link>
                    </div>
                  </div>
                  <div className="dropdown-btns dropdown-btns__footer">
                    <Button className="account-logout" variant="secondary" onClick={userLogout}>
                      Sign out
                    </Button>
                  </div>
                  {impersonator?.userId && (
                    <>
                      <div className="header logged-in-as impersonating">
                        Impersonator <span>{impersonator.email}</span>
                      </div>
                      <div className="dropdown-impersonator">
                        <Button
                          className="account-stop-impersonating"
                          onClick={() => dispatch(stopImpersonating())}
                          variant="primary"
                        >
                          Stop Impersonating
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
      >
        <div className="dropdown">
          <div
            className={clsx('dropdown-trigger', open && 'active')}
            onClick={toggleDropdownVisible}
            onKeyDown={toggleDropdownVisibleKey}
            role="button"
            tabIndex={0}
          >
            <div className="grid-row">
              <div className="user-logo-container">
                <div className="user-logo current">
                  <div className="user-logo-letter">
                    {selectedOrg.isMyAccount ? accountName.charAt(0) : selectedOrg.initial}
                  </div>
                </div>
                <div className="user-name">
                  {selectedOrg.isMyAccount ? accountName : selectedOrg.name}
                  <Icon
                    className="user-name__icon"
                    name={open ? 'ChevronSingleNorth' : 'ChevronSingleSouth'}
                    size="small"
                    fill="DdsColorInteractiveSecondaryDefault"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DropdownPanel>
    </div>
  );
};

export default OrgChange;
